import React, { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, RotateCcw } from 'react-feather';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState, ConfirmPopover } from '../../components/ConfigPages';
import { InlineSelect, InlineInput, InlineCheckbox, BulkActionBar, StatusDot, SortIcon } from '../../components/InlineTable';
import { updatePosition, deletePosition, restorePosition, bulkUpdatePositions } from '../../utils/positionsApi';

const BULK_FIELDS = [
  { key: 'departmentId', label: 'Department' },
  { key: 'typeClassificationId', label: 'Classification' },
  { key: 'revenueBasisGroupId', label: 'Revenue basis' },
];

const barSelectStyle = {
  background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)',
  borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', cursor: 'pointer',
  fontFamily: 'var(--font-sans)', color: 'rgba(255,255,255,.95)',
};

/** Field + value picker for the dark BulkActionBar (three fields, one apply). */
function BulkFieldPicker({ optionsByField, onApply, applying }) {
  const [field, setField] = useState('typeClassificationId');
  const [value, setValue] = useState('');
  const options = optionsByField[field] || [];
  const canApply = value !== '' && !applying;
  return (
    <>
      <span style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', whiteSpace: 'nowrap' }}>Set</span>
      <select value={field} onChange={(e) => { setField(e.target.value); setValue(''); }} disabled={applying} style={barSelectStyle} aria-label="Field to set">
        {BULK_FIELDS.map((f) => <option key={f.key} value={f.key} style={{ color: 'rgba(0,0,0,.9)' }}>{f.label}</option>)}
      </select>
      <span style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', whiteSpace: 'nowrap' }}>to</span>
      <select value={value} onChange={(e) => setValue(e.target.value)} disabled={applying} style={{ ...barSelectStyle, minWidth: 168, color: value ? 'rgba(255,255,255,.95)' : 'rgba(255,255,255,.3)' }} aria-label="Value">
        <option value="">Choose…</option>
        <option value="__none__" style={{ color: 'rgba(0,0,0,.9)' }}>(none — clear)</option>
        {options.map((o) => <option key={o.value} value={o.value} style={{ color: 'rgba(0,0,0,.9)' }}>{o.label}</option>)}
      </select>
      <button
        type="button"
        disabled={!canApply}
        onClick={() => { if (!canApply) return; onApply(field, value === '__none__' ? null : Number(value)); setValue(''); }}
        style={{
          padding: '5px 14px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-sans)',
          cursor: canApply ? 'pointer' : 'not-allowed', background: canApply ? 'var(--lodge-orange-400)' : 'rgba(255,255,255,.07)',
          color: canApply ? 'rgba(255,255,255,.95)' : 'rgba(255,255,255,.2)',
        }}
      >
        {applying ? 'Applying…' : 'Apply'}
      </button>
    </>
  );
}

function PositionRow({
  position, selected, onToggle, departmentOptions, classificationOptions, revenueBasisOptions,
  landsIn, onField, onDelete, onRestore, deleting,
}) {
  const [hov, setHov] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const act = hov || selected;
  const dead = !!position.isDeleted;
  const hasDept = !!position.departmentId;
  const hasClass = !!position.typeClassificationId;
  const state = dead ? 'none' : hasDept && hasClass ? 'complete' : (hasDept || hasClass) ? 'partial' : 'none';
  const deptLabel = departmentOptions.find((o) => o.value === position.departmentId)?.label;
  const classLabel = classificationOptions.find((o) => o.value === position.typeClassificationId)?.label;
  const title = dead ? 'Deleted — excluded from labor reporting'
    : state === 'complete' ? `Mapped to ${deptLabel} · ${classLabel}`
    : !hasDept && !hasClass ? 'No department or classification — excluded from labor dashboards'
    : !hasDept ? `Needs a department (classified as ${classLabel})`
    : `Needs a classification (in ${deptLabel})`;

  return (
    <tr
      className={`cp-row${selected ? ' it-row-selected' : ''}${dead ? ' it-row-deleted' : ''}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <td style={{ width: 44 }} className="cp-cell">
        <InlineCheckbox checked={selected} onChange={() => onToggle(position.id)} disabled={dead} />
      </td>
      <td style={{ minWidth: 200 }}>
        <div className="cp-cell">
          <div className="cp-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{position.name || '—'}</div>
          {position.code ? <div className="cp-code">{position.code}</div> : null}
        </div>
      </td>
      <td style={{ minWidth: 200 }}>
        <InlineInput value={position.displayName || ''} onChange={async (v) => { const ok = await onField(position.id, 'displayName', v.trim() || null); if (!ok) throw new Error('save failed'); }} placeholder="Display name…" rowActive={act} disabled={dead} ariaLabel="Display name" />
      </td>
      <td style={{ minWidth: 220 }}>
        <InlineSelect value={position.departmentId ?? null} options={departmentOptions} onChange={(v) => onField(position.id, 'departmentId', v ?? null)} placeholder="Department…" rowActive={act} disabled={dead} ariaLabel="Department" />
        {position.departmentId && landsIn ? <div className="cp-lands">lands in <strong>{landsIn}</strong></div> : null}
      </td>
      <td style={{ minWidth: 180 }}>
        <InlineSelect value={position.typeClassificationId ?? null} options={classificationOptions} onChange={(v) => onField(position.id, 'typeClassificationId', v ?? null)} placeholder="Classification…" rowActive={act} disabled={dead} ariaLabel="Classification" />
      </td>
      <td style={{ minWidth: 200 }}>
        <InlineSelect value={position.revenueBasisGroupId ?? null} options={revenueBasisOptions} onChange={(v) => onField(position.id, 'revenueBasisGroupId', v ?? null)} placeholder="Same as classification" rowActive={act} disabled={dead} minWidth={240} ariaLabel="Revenue basis" />
      </td>
      <td style={{ width: 44, textAlign: 'center' }}>
        <StatusDot state={state} title={title} />
      </td>
      <td style={{ width: 56 }}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', paddingRight: 8 }}>
          <div className="cp-row-actions">
            {dead ? (
              <ToolbarButton small iconOnly variant="ghost" icon={RotateCcw} title="Restore position" aria-label="Restore position" onClick={() => onRestore(position)} disabled={deleting} />
            ) : (
              <ToolbarButton small iconOnly variant="ghost" icon={Trash2} title="Delete position" aria-label="Delete position" onClick={() => setConfirm(true)} disabled={deleting} />
            )}
          </div>
          <ConfirmPopover
            open={confirm}
            onClose={() => setConfirm(false)}
            onConfirm={async () => { await onDelete(position); setConfirm(false); }}
            title={`Delete ${position.name}?`}
            body="It disappears from labor reporting until restored. Payroll rows keep their history."
            confirmLabel="Delete"
            busy={deleting}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * Positions tab — payroll positions → department + classification (+ revenue basis).
 * Owns filter/sort/selection state; the page owns the data and reload helpers.
 */
export default function PositionsTab({
  orgId,
  positions,            // ALL rows incl. soft-deleted
  setPositions,
  departments,
  departmentOptions,
  classificationOptions,
  revenueBasisOptions,
  revenueBasisGroups,
  typeClassifications,
  landsInByDepartmentId, // departmentId → "40000 · Rooms" string (current location) or undefined
  status, setStatus,
  onOpenDepartments,
  onAddDepartment,
}) {
  const [q, setQ] = useState('');
  const [deptFilter, setDeptFilter] = useState(null);
  const [classFilter, setClassFilter] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sort, setSort] = useState({ key: 'name', direction: 'ascending' });
  const [orderSnapshot, setOrderSnapshot] = useState(null); // freezes order while editing the sorted column
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const live = useMemo(() => positions.filter((p) => !p.isDeleted), [positions]);
  const deletedCount = positions.length - live.length;
  const isUnmapped = (p) => !p.departmentId || !p.typeClassificationId;

  const deptName = useCallback((id) => departments.find((d) => d.id === id)?.name || '', [departments]);
  const className = useCallback((id) => typeClassifications.find((t) => t.id === id)?.name || '', [typeClassifications]);
  const basisName = useCallback((id) => revenueBasisGroups.find((g) => g.id === id)?.name || '', [revenueBasisGroups]);

  const sortValue = useCallback((p, key) => {
    if (key === 'departmentId') return deptName(p.departmentId).toLowerCase();
    if (key === 'typeClassificationId') return className(p.typeClassificationId).toLowerCase();
    if (key === 'revenueBasisGroupId') return basisName(p.revenueBasisGroupId).toLowerCase();
    return String(p[key] ?? '').toLowerCase();
  }, [deptName, className, basisName]);

  const sortRows = useCallback((rows, cfg) => [...rows].sort((a, b) => {
    const av = sortValue(a, cfg.key); const bv = sortValue(b, cfg.key);
    if (av < bv) return cfg.direction === 'ascending' ? -1 : 1;
    if (av > bv) return cfg.direction === 'ascending' ? 1 : -1;
    return 0;
  }), [sortValue]);

  const requestSort = (key) => {
    const direction = sort.key === key && sort.direction === 'ascending' ? 'descending' : 'ascending';
    setSort({ key, direction });
    setOrderSnapshot(null);
  };

  // Freeze the current order before an optimistic edit to the sorted column so
  // the row you just edited does not jump away from under the cursor.
  const freezeOrderIfSortedBy = (field) => {
    if (sort.key !== field || orderSnapshot) return;
    setOrderSnapshot({ direction: sort.direction, orderedIds: sortRows(positions, sort).map((p) => p.id) });
  };

  const rows = useMemo(() => {
    let out = showDeleted ? positions : live;
    if (status === 'unmapped') out = out.filter((p) => p.isDeleted || isUnmapped(p));
    else if (status === 'mapped') out = out.filter((p) => !p.isDeleted && !isUnmapped(p));
    if (deptFilter != null) out = out.filter((p) => p.departmentId === deptFilter);
    if (classFilter != null) out = out.filter((p) => p.typeClassificationId === classFilter);
    if (q) {
      const n = q.toLowerCase();
      out = out.filter((p) =>
        p.name?.toLowerCase().includes(n) || p.displayName?.toLowerCase().includes(n) || p.code?.toLowerCase().includes(n)
        || deptName(p.departmentId).toLowerCase().includes(n) || className(p.typeClassificationId).toLowerCase().includes(n)
        || basisName(p.revenueBasisGroupId).toLowerCase().includes(n));
    }
    if (orderSnapshot && orderSnapshot.direction === sort.direction) {
      const idx = new Map(orderSnapshot.orderedIds.map((id, i) => [id, i]));
      return [...out].sort((a, b) => (idx.get(a.id) ?? Infinity) - (idx.get(b.id) ?? Infinity));
    }
    return sortRows(out, sort);
  }, [positions, live, showDeleted, status, deptFilter, classFilter, q, sort, orderSnapshot, sortRows, deptName, className, basisName]);

  const counts = useMemo(() => ({
    all: live.length,
    unmapped: live.filter(isUnmapped).length,
    mapped: live.filter((p) => !isUnmapped(p)).length,
  }), [live]);

  const visibleSelectable = useMemo(() => rows.filter((p) => !p.isDeleted).map((p) => p.id), [rows]);
  const allSelected = visibleSelectable.length > 0 && visibleSelectable.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && visibleSelectable.some((id) => selectedIds.has(id));
  const selectedVisible = visibleSelectable.filter((id) => selectedIds.has(id));

  const toggleAll = () => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (allSelected) visibleSelectable.forEach((id) => next.delete(id)); else visibleSelectable.forEach((id) => next.add(id));
    return next;
  });
  const toggleOne = (id) => setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const clearSelection = () => setSelectedIds(new Set());

  const patchLocal = (ids, patch) => setPositions((prev) => prev.map((p) => (ids.has(p.id) ? { ...p, ...patch } : p)));

  const FIELD_LABEL = { displayName: 'Display name', departmentId: 'Department', typeClassificationId: 'Classification', revenueBasisGroupId: 'Revenue basis' };

  // Single-field inline edit: optimistic, sends ONLY the changed field.
  const onField = async (positionId, field, value) => {
    const position = positions.find((p) => p.id === positionId);
    if (!position || (position[field] ?? null) === value) return true;
    freezeOrderIfSortedBy(field);
    patchLocal(new Set([positionId]), { [field]: value });
    try {
      await updatePosition({ id: positionId, [field]: value });
      toast.success(`${FIELD_LABEL[field]} ${value == null ? 'cleared' : 'updated'}`);
      return true;
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      toast.error(`Could not update ${FIELD_LABEL[field].toLowerCase()} — try again`);
      patchLocal(new Set([positionId]), { [field]: position[field] ?? null });
      return false;
    }
  };

  const onBulkApply = async (field, value) => {
    const ids = selectedVisible;
    if (ids.length === 0) { toast.error('No positions selected'); return; }
    const before = new Map(positions.filter((p) => selectedIds.has(p.id)).map((p) => [p.id, p[field] ?? null]));
    freezeOrderIfSortedBy(field);
    setBulkApplying(true);
    patchLocal(new Set(ids), { [field]: value });
    try {
      const res = await bulkUpdatePositions({ orgId, ids, field, value });
      const label = value == null ? '(none)' : (
        field === 'departmentId' ? deptName(value) : field === 'typeClassificationId' ? className(value) : basisName(value)
      ) || value;
      toast.success(`Set ${FIELD_LABEL[field].toLowerCase()} to "${label}" on ${res?.updated ?? ids.length} positions`);
      clearSelection();
    } catch (err) {
      console.error('Bulk update failed:', err);
      toast.error(`Could not update ${ids.length} positions — nothing was changed`);
      setPositions((prev) => prev.map((p) => (before.has(p.id) ? { ...p, [field]: before.get(p.id) } : p)));
    } finally {
      setBulkApplying(false);
    }
  };

  const onDelete = async (position) => {
    setBusyId(position.id);
    try {
      await deletePosition(position.id);
      patchLocal(new Set([position.id]), { isDeleted: true });
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(position.id); return n; });
      toast.success(`Deleted ${position.name}`);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(`Could not delete ${position.name} — try again`);
    } finally { setBusyId(null); }
  };
  const onRestore = async (position) => {
    setBusyId(position.id);
    try {
      await restorePosition(position.id);
      patchLocal(new Set([position.id]), { isDeleted: false });
      toast.success(`Restored ${position.name}`);
    } catch (err) {
      console.error('Restore failed:', err);
      toast.error(`Could not restore ${position.name} — try again`);
    } finally { setBusyId(null); }
  };

  const optionsByField = { departmentId: departmentOptions, typeClassificationId: classificationOptions, revenueBasisGroupId: revenueBasisOptions };
  const th = (key, label, style) => (
    <th className="it-sort" style={style} onClick={() => requestSort(key)}>{label} <SortIcon columnKey={key} sortKey={sort.key} direction={sort.direction} /></th>
  );

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: q, onChange: setQ, placeholder: 'Search position, code, department…' }}
        status={{ value: status, counts, onChange: setStatus }}
        chips={(
          <>
            <div style={{ width: 190 }}>
              <InlineSelect rowActive value={deptFilter} options={departmentOptions} onChange={setDeptFilter} placeholder="Department: all" minWidth={200} ariaLabel="Filter by department" />
            </div>
            <div style={{ width: 190 }}>
              <InlineSelect rowActive value={classFilter} options={classificationOptions} onChange={setClassFilter} placeholder="Classification: all" minWidth={200} ariaLabel="Filter by classification" />
            </div>
          </>
        )}
        showDeleted={{ value: showDeleted, onChange: setShowDeleted, count: deletedCount }}
        meta={`${live.length.toLocaleString()} positions`}
        actions={(
          <>
            <ToolbarButton icon={Edit2} onClick={onOpenDepartments}>Departments</ToolbarButton>
            <ToolbarButton variant="primary" icon={Plus} onClick={onAddDepartment}>Add department</ToolbarButton>
          </>
        )}
      />
      {selectedVisible.length > 0 && (
        <BulkActionBar count={selectedVisible.length} noun={`position${selectedVisible.length !== 1 ? 's' : ''} selected`} onClear={clearSelection}>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,.12)', flexShrink: 0 }} />
          <BulkFieldPicker optionsByField={optionsByField} onApply={onBulkApply} applying={bulkApplying} />
        </BulkActionBar>
      )}
      {rows.length === 0 ? (
        <ConfigEmptyState
          title={live.length === 0 ? 'No positions yet' : 'No positions match'}
          body={live.length === 0
            ? 'Positions arrive from the payroll feed. Once a payroll file lands they appear here for mapping.'
            : 'Try clearing the search or the filters.'}
          action={(q || deptFilter != null || classFilter != null || status !== 'all') && live.length > 0
            ? <ToolbarButton variant="ghost" onClick={() => { setQ(''); setDeptFilter(null); setClassFilter(null); setStatus('all'); }}>Clear filters</ToolbarButton>
            : null}
        />
      ) : (
        <div className="it-scroll">
          <table className="it-tbl" style={{ minWidth: 1040 }}>
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <InlineCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} disabled={visibleSelectable.length === 0} title={`Select all ${visibleSelectable.length} visible positions`} />
                </th>
                {th('name', 'Position')}
                {th('displayName', 'Display name', { minWidth: 200 })}
                {th('departmentId', 'Department', { minWidth: 220 })}
                {th('typeClassificationId', 'Classification', { minWidth: 180 })}
                {th('revenueBasisGroupId', 'Revenue basis', { minWidth: 200 })}
                <th style={{ width: 44, textAlign: 'center' }}>Status</th>
                <th style={{ width: 56 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <PositionRow
                  key={p.id}
                  position={p}
                  selected={selectedIds.has(p.id)}
                  onToggle={toggleOne}
                  departmentOptions={departmentOptions}
                  classificationOptions={classificationOptions}
                  revenueBasisOptions={revenueBasisOptions}
                  landsIn={landsInByDepartmentId(p.departmentId)}
                  onField={onField}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  deleting={busyId === p.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
